import React from 'react';

interface INpsScoreScaleProps {
  selectedScore: number | null;
  onScoreSelect: (score: number) => void;
}

type RagBand = 'detractor' | 'passive' | 'promoter';

const SadFace: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    <path d="M8 17c1-2 3-3 4-3s3 1 4 3" />
  </svg>
);

const NeutralFace: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    <line x1="8" y1="15.5" x2="16" y2="15.5" />
  </svg>
);

const HappyFace: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    <path d="M8 14c1 2.5 3 3.5 4 3.5s3-1 4-3.5" />
  </svg>
);

const getBand = (score: number): RagBand => {
  if (score <= 6) return 'detractor';
  if (score <= 8) return 'passive';
  return 'promoter';
};

const getFaceIcon = (score: number): React.ReactNode => {
  const band = getBand(score);
  if (band === 'detractor') return <SadFace />;
  if (band === 'passive') return <NeutralFace />;
  return <HappyFace />;
};

const SCORES: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const NpsScoreScale: React.FC<INpsScoreScaleProps> = ({
  selectedScore,
  onScoreSelect,
}) => {
  return (
    <div>
      <div
        className="nps-score-scale"
        role="radiogroup"
        aria-label="NPS score selection"
      >
        {SCORES.map((score) => {
          const band = getBand(score);
          const isSelected = selectedScore === score;
          return (
            <button
              key={score}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`Score ${score}`}
              className={[
                'nps-score-button',
                band,
                isSelected ? 'selected' : '',
              ].join(' ')}
              onClick={() => onScoreSelect(score)}
            >
              {score}
            </button>
          );
        })}
      </div>
      <div className="nps-emoji-row" aria-hidden="true">
        {SCORES.map((score) => {
          const isGroupStart = score === 0 || score === 7 || score === 9;
          const isGroupEnd = score === 6 || score === 8 || score === 10;
          const cls = [
            'nps-score-emoji',
            isGroupStart ? 'group-start' : '',
            isGroupEnd ? 'group-end' : '',
          ].join(' ');
          return (
            <span key={score} className={cls}>
              {getFaceIcon(score)}
            </span>
          );
        })}
      </div>
      <div className="nps-scale-labels">
        <span>Highly unlikely</span>
        <span>Extremely likely</span>
      </div>
    </div>
  );
};

export default NpsScoreScale;
